import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { useUserLanguage } from '@/customHooks';
import { NormalFormTextField } from '../molecules';
import { SquareButton2 } from '../atoms';
import { useCreatePropertyContext } from '@/customHooks/useCreatePropertyContext';
import { PropertyTypeOption } from '@/graphql/generated';
import textsData from '@/textData/CreatePropertyTexts.json';

// Icons
import BuildingIcon from '@/assets/property-icons/building-icon.svg';
import CityIcon from '@/assets/property-icons/city-icon.svg';
import HouseIcon from '@/assets/property-icons/house-icon.svg';
import HousesIcon from '@/assets/property-icons/houses-icon.svg';
import TownhouseIcon from '@/assets/property-icons/townhouse-icon.svg';

const FieldContainer = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  padding: 2rem;
  margin-bottom: 3rem;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  width: 400px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

type FormData = {
  name: string;
  propertyType: PropertyTypeOption | null;
};

export const SelectPropertyTypeMenuCard = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.propertyTypeForm : textsData.en.propertyTypeForm;

  const { setPropertyType, setName, setButtonEnabled, setButtonDisabled, state: contextState } = useCreatePropertyContext();

  const [formData, setFormData] = useState<FormData>({
    name: contextState.name || '',
    propertyType: contextState.propertyType || null,
  });

  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
    if (formData.name && formData.propertyType) {
      setButtonEnabled();
    } else {
      setButtonDisabled();
    }
  }, [formData, setButtonEnabled, setButtonDisabled]);

  useEffect(() => {
    // Save to context on unmount
    return () => {
      setName(formDataRef.current.name);
      if (formDataRef.current.propertyType) {
        setPropertyType(formDataRef.current.propertyType);
      }
    };
  }, [setName, setPropertyType]);

  const handleNameChange = (value: string | null) => {
    setFormData(prev => ({ ...prev, name: value || '' }));
  };

  const handleTypeSelect = (value: PropertyTypeOption) => {
    setFormData(prev => ({ ...prev, propertyType: value }));
  };

  const buttons = useMemo(() => [
    { text: texts.unique, icon: HouseIcon, value: PropertyTypeOption.Unique },
    { text: texts.multiFamily, icon: HousesIcon, value: PropertyTypeOption.MultiFamily },
    { text: texts.apartment, icon: BuildingIcon, value: PropertyTypeOption.Apartment },
    { text: texts.townHouse, icon: TownhouseIcon, value: PropertyTypeOption.TownHouse },
    { text: texts.other, icon: CityIcon, value: PropertyTypeOption.Other },
  ], [texts]);

  return (
    <div>
      <Row justify="center">
        <Col xs="content">
          <FieldContainer>
            <NormalFormTextField
              label={texts.name.label}
              placeholder={texts.name.placeholder}
              value={formData.name}
              onChange={handleNameChange}
            />
          </FieldContainer>
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonsContainer>
            {buttons.map((btn) => (
              <SquareButton2
                key={btn.value}
                action={() => handleTypeSelect(btn.value)}
                selected={formData.propertyType === btn.value}
              >
                <><img src={btn.icon} alt={btn.text} /><span>{btn.text}</span></>
              </SquareButton2>
            ))}
          </ButtonsContainer>
        </Col>
      </Row>
    </div>
  );
};
